// native
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

// addon
import StyleFilter from "@readcube/citation-style-filter";

// service
import { ApiService } from './api.service';
import { GlobalsService } from './globals.service';
import { LegacyService } from './legacy.service';
import { UtilityService } from './utility.service';

// models
import { IStyle, StylesInfo, IDocumentStyle, StyleRequestBody } from '../../models';

// constants
import { DEFAULT_STYLE } from '../../constants/constants';

@Injectable({
  providedIn: 'root'
})
export class StylesService {

  styleFilter: any;
  // using behaviour subject as components need to have last value if they get destroyed and rerendered
  stylesInfo$: BehaviorSubject<StylesInfo> = new BehaviorSubject<StylesInfo>(null);
  stylesLoading: boolean = true;

  clientFavoritesStyles: IStyle[] = [];
  clientCustomFavoritesStyles: IStyle[] = [];

  isFavoritesView: boolean = false;

  selectedStyle: IStyle;
  userStyle: IStyle;
  documentStyle: IStyle;

  constructor(
    private apiService: ApiService,
    private globalsService: GlobalsService,
    private httpClient: HttpClient,
    private legacyService: LegacyService,
    private utilityService: UtilityService
  ) { }

  calculateStyles(documentStyleTitle: string) {
    this.getStyleSettings().subscribe((userStylesInfo: StylesInfo) => {
      const favoritesIds: string[] = [];
      const userStyleId = userStylesInfo.default_style_id;

      userStylesInfo.favorite_style_ids.forEach((item: string) => {
        favoritesIds.push(item);
      });

      this.styleFilter = new StyleFilter(() => this.getMergedStylesList(favoritesIds, documentStyleTitle, userStyleId, userStylesInfo));
      this.styleFilter.preload();
    }, err => {
      this.stylesLoading = false;
    });
  }

  private getMergedStylesList(favoritesIds: string[], documentStyleTitle: string, userStyleId: string, userStyleInfo: StylesInfo): Promise<IStyle[] | void> {
    return this.httpClient.get(`${this.globalsService.appConfig.environment.baseUrls.smartciteAssets}/merged-styles.json`, {
      headers: { 'Content-Type': 'text/plain' }
    })
      .toPromise()
      .then(async (styles: IStyle[]) => {
        // user style, document style and favorites list are matched against merged styles list file to extract additional style data
        // as only title (unique style id that doesn't change) is persisted in document
        this.calculateSelectedStyleFromMergedStyles(styles, favoritesIds, documentStyleTitle, userStyleId);

        // user style and document style are matched against custom styles list file to extract additional style data
        this.calculateSelectedStyleFromCustomStyles(favoritesIds, documentStyleTitle, userStyleId, userStyleInfo);

        this.selectedStyle = this.documentStyle || this.userStyle || DEFAULT_STYLE;

        await this.convertLegacyOnMergedStylesLoad();

        this.stylesInfo$.next(userStyleInfo);
        this.stylesLoading = false;

        return styles;
      })
      .catch(err => {
        this.stylesLoading = false;
      });
  }

  private calculateSelectedStyleFromMergedStyles(styles: IStyle[], favoritesIds: string[], documentStyleTitle: string, userStyleId: string) {
    styles.forEach((styleItem: IStyle) => {
      styleItem.isFavorite = favoritesIds.includes(styleItem.id);
      styleItem.isFavorite && this.clientFavoritesStyles.push(styleItem);

      if (userStyleId && userStyleId === styleItem.id) {
        this.userStyle = styleItem;
      }

      if (documentStyleTitle && documentStyleTitle === styleItem.title) {
        this.documentStyle = styleItem;
      }
    });
  }

  private calculateSelectedStyleFromCustomStyles(favoritesIds: string[], documentStyleTitle: string, userStyleId: string, userStylesInfo: StylesInfo) {
    const favoriteCustomStyles = [];
    userStylesInfo.custom_styles.forEach(style => {
      style.isFavorite = favoritesIds.includes(style.id);
      style.isFavorite && favoriteCustomStyles.push(style);

      if (userStyleId === style.id) {
        this.userStyle = style;
      }

      if (documentStyleTitle === style.title) {
        this.documentStyle = style;
      }
    });
    this.utilityService.sortAlphabetically(favoriteCustomStyles, 'title');
    this.setClientCustomFavoriteStyles(favoriteCustomStyles);
  }

  private async convertLegacyOnMergedStylesLoad() {
    const style = await this.getDocumentStyleFormatFromSelectedStyle();
    this.legacyService.convertLegacy(style, this.globalsService.locale, this.globalsService.language);
  }

  private getStyleSettings(): Observable<StylesInfo> {
    const url = `${this.globalsService.appConfig.environment.baseUrls.services}/citation_styles/info`;

    return this.apiService.get(url);
  }

  addFavoriteStyle(styleId: string): Observable<StylesInfo> {
    const url = `${this.globalsService.appConfig.environment.baseUrls.services}/citation_styles/add_favorite`;

    const body: StyleRequestBody = { style_id: styleId };

    return this.apiService.post(url, body);
  }

  removeFavoriteStyle(styleId: string): Observable<StylesInfo> {
    const url = `${this.globalsService.appConfig.environment.baseUrls.services}/citation_styles/remove_favorite`;

    const body: StyleRequestBody = { style_id: styleId };

    return this.apiService.post(url, body);
  }

  setDefaultStyle(styleId: string): Observable<StylesInfo> {
    const url = `${this.globalsService.appConfig.environment.baseUrls.services}/citation_styles/set_default`;

    const body: StyleRequestBody = { style_id: styleId };

    return this.apiService.post(url, body);
  }

  getSelectedStyle(): IStyle {
    return this.selectedStyle;
  }

  setSelectedStyle(style: IStyle): void {
    this.selectedStyle = style;
  }

  getClientFavoriteStyles(): IStyle[] {
    return this.clientFavoritesStyles;
  }

  getClientFavoriteCustomStyles(): IStyle[] {
    return this.clientCustomFavoritesStyles;
  }

  setClientFavoriteStyles(styles: IStyle[]): void {
    this.clientFavoritesStyles = styles;
  }

  setClientCustomFavoriteStyles(styles: IStyle[]): void {
    this.clientCustomFavoritesStyles = styles;
  }

  async getDocumentStyleFormatFromSelectedStyle(): Promise<IDocumentStyle> {
    const body = await this.apiService.getTextFile((<IStyle>this.selectedStyle)?.href);
    return {
      title: this.selectedStyle.title,
      body
    };
  }
}